import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import * as menuApi from '../../shared/api/menus.js'
import type { Menu } from '../../shared/types/models.js'
import { canManageCatalog } from '../../shared/utils/permissions.js'
import { ROUTES } from '../../app/routes.js'
import MenuCard from './components/MenuCard.js'
import CreateMenuForm from './components/CreateMenuForm.js'
import EmptyState from './components/EmptyState.js'

type ListStatus = 'loading' | 'ready' | 'error'

export default function MenuPage() {
  const { t } = useTranslation()

  const orgId = useOrgStore(s => s.activeOrganization)?.id
  const role = useOrgStore(s => s.activeOrganization)?.role
  const canManage = canManageCatalog(role)

  const [menus, setMenus] = useState<Menu[]>([])
  const [status, setStatus] = useState<ListStatus>('loading')
  const [showCreate, setShowCreate] = useState(false)

  async function load() {
    if (!orgId) return
    setStatus('loading')
    try {
      const res = await menuApi.getMenus(orgId)
      setMenus(res.data)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }

  useEffect(() => {
    void load()
  }, [orgId])

  if (!orgId) return null

  if (status === 'loading') {
    return (
      <section className="page-section">
        <h2>{t('app.menu')}</h2>
        <div className="page-loading">
          <div className="spinner" />
        </div>
      </section>
    )
  }

  if (status === 'error') {
    return (
      <section className="page-section">
        <h2>{t('app.menu')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('menu.loadErrorList')}</div>
          <button type="button" className="btn btn-primary" onClick={() => void load()}>
            {t('error.retry')}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <h2>{t('app.menu')}</h2>
          <p className="page-section-description">{t('menu.listSubtitle')}</p>
        </div>
        {canManage && !showCreate && (
          <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
            {t('menu.create')}
          </button>
        )}
      </div>

      {canManage && showCreate && (
        <CreateMenuForm organizationId={orgId} onCancel={() => setShowCreate(false)} />
      )}

      {menus.length === 0 ? (
        <EmptyState
          title={t('menu.empty')}
          hint={t('menu.emptyHint')}
          actionLabel={canManage && !showCreate ? t('menu.create') : undefined}
          onAction={canManage && !showCreate ? () => setShowCreate(true) : undefined}
        />
      ) : (
        <div className="menu-grid">
          {menus.map(menu => (
            <MenuCard key={menu.id} menu={menu} to={`${ROUTES.MENU}/${menu.id}`} />
          ))}
        </div>
      )}
    </section>
  )
}